import { PlaidAccountModel, PlaidItemModel } from '../../app/reduxToolkit/plaidSlice'

/**
 * Plaid Hosted Link flow for the in-app (Expo WebView) client.
 *
 * Plaid Link's OAuth handoff can't complete inside a mobile WebView, so the
 * in-app client uses Hosted Link: the native shell opens Plaid's hosted page
 * in a plain in-app browser (SFSafariViewController / Chrome Custom Tab).
 *
 * Completion is NOT detected via a redirect. Earlier attempts failed because
 * Plaid rejects a custom-scheme completion_redirect_uri (INVALID_FIELD), and
 * an https redirect can't reliably hand control back to a WebView app. So
 * instead, while the user is in the browser, this polls the backend
 * (/backend/plaid/exchange-hosted-link), which reads the finished Plaid Link
 * session server-side and returns the item once it's done (409 until then).
 * The public_token never round-trips through the browser.
 *
 * Bridge protocol (mirrors the push-notification bridge):
 *  - web → native: postMessage { type: 'open-plaid-hosted-link', data: { url } }
 *  - native → web: window CustomEvent 'plaid-hosted-link-result' with
 *                  detail 'error' — sent ONLY if the browser couldn't be
 *                  opened, so we can stop polling instead of timing out.
 */

// The web → native bridge (window.isExpoApp / window.ReactNativeWebView) lives
// in ../nativeBridge. Re-export `isExpoApp` so existing importers of it from
// this module keep working.
import { postToNative } from '../nativeBridge'
export { isExpoApp } from '../nativeBridge'

// Bank OAuth (MFA, account selection) can take a while, so poll patiently.
// Each poll is a cheap server-side session read; it 409s until the session
// finishes. Cadence stays well under the endpoint's rate limit.
const POLL_INTERVAL_MS = 5000
const POLL_MAX_MS = 10 * 60 * 1000

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

interface ConnectedPayload {
  plaid_item_id: string
  institution_name: string | null
  sync_started_at: string | null
  accounts: PlaidAccountModel[]
}

export type HostedLinkOutcome =
  | { status: 'cancelled' }
  | { status: 'connected'; data: ConnectedPayload }
  | { status: 'reconnected'; item: PlaidItemModel }

/**
 * Drive the full Hosted Link round trip: mint a hosted link token, ask the
 * native shell to open it in an in-app browser, then poll the backend until
 * the Plaid session finishes (or the user abandons it). Pass `itemId` for
 * update mode (reconnecting an existing item).
 */
export async function runPlaidHostedLink(opts: {
  authHeaders: Record<string, string>
  membershipId: string
  itemId?: string
}): Promise<HostedLinkOutcome> {
  const { authHeaders, membershipId, itemId } = opts

  const createResp = await fetch('/backend/plaid/create-link-token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      membership_id: membershipId,
      hosted: true,
      ...(itemId ? { item_id: itemId } : {}),
    }),
  })
  if (!createResp.ok) {
    const detail = await createResp.text().catch(() => 'unknown error')
    throw new Error(`create-link-token returned ${createResp.status}: ${detail}`)
  }
  const created = await createResp.json()
  if (!created.link_token || !created.hosted_link_url) {
    throw new Error('No hosted_link_url in create-link-token response')
  }

  if (!window.ReactNativeWebView) {
    throw new Error('Native bridge unavailable')
  }

  // The native side only signals back if it couldn't open the browser at
  // all; otherwise completion is determined purely by polling below.
  let openFailed = false
  const onResult = (event: Event) => {
    if ((event as CustomEvent).detail === 'error') openFailed = true
  }
  window.addEventListener('plaid-hosted-link-result', onResult)

  try {
    // Bridge presence is asserted above (throwing when absent, unlike the
    // no-op postToNative), so this always actually posts here.
    postToNative('open-plaid-hosted-link', { url: created.hosted_link_url })

    const attempts = Math.ceil(POLL_MAX_MS / POLL_INTERVAL_MS)
    for (let i = 0; i < attempts; i++) {
      await delay(POLL_INTERVAL_MS)
      if (openFailed) {
        throw new Error('Native shell could not open the Plaid browser')
      }

      const exchangeResp = await fetch('/backend/plaid/exchange-hosted-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          link_token: created.link_token,
          membership_id: membershipId,
          ...(itemId ? { item_id: itemId } : {}),
        }),
      })

      // 409 = the Plaid session hasn't produced an item yet (user still in
      // the flow, or hasn't started). Keep polling.
      if (exchangeResp.status === 409) {
        continue
      }
      if (!exchangeResp.ok) {
        const detail = await exchangeResp.text().catch(() => 'unknown error')
        throw new Error(`exchange-hosted-link returned ${exchangeResp.status}: ${detail}`)
      }

      const data = await exchangeResp.json()
      if (itemId) {
        return { status: 'reconnected', item: data.item as PlaidItemModel }
      }
      return { status: 'connected', data: data as ConnectedPayload }
    }

    // Polling window elapsed without the session finishing — treat as the
    // user abandoning the flow.
    return { status: 'cancelled' }
  } finally {
    window.removeEventListener('plaid-hosted-link-result', onResult)
  }
}
